import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { abortDocumentReaderUpload } from '@commenda/api/commenda-ai/document-reader/abortDocumentReaderUpload';
import {
  DocumentReaderUploadStatus,
  type IDocumentReaderUploadLimits,
  type IDocumentReaderUploadReference,
} from '@commenda/api/commenda-ai/document-reader/types';
import type { CommendaAiUploadFile } from '@commenda/api/commenda-ai/types';
import { toast } from '@commenda/kakashi';

import { DataRoomResumeRecordHelper } from './DataRoomResumeRecordHelper';
import { DataRoomUploadHelper } from './DataRoomUploadHelper';
import { DataRoomUploadTransfer } from './DataRoomUploadTransfer';
import {
  DataRoomDropKind,
  DataRoomFileState,
  type IDataRoomFileOutcome,
  type IDataRoomFileProgress,
  type IDataRoomLocalFile,
  type IDataRoomResumeRecord,
  type IDataRoomUploadSession,
  type TDataRoomRecordUpdate,
} from './types';
import { useDataRoomFileRows } from './useDataRoomFileRows';

interface IUseDataRoomUploadParams {
  uploadLimits: IDocumentReaderUploadLimits | null;
  conversationId: string | null;
  companyId: number | null;
}

const UNEXPECTED_STOP_ERROR = 'The document upload stopped unexpectedly.';
const READER_OFF_NOTICE =
  'Documents were removed because this agent does not read documents.';

/** One data room per message: creates the upload, adds later drops to it, removes single files, sends
 * parts in parallel with retries, resumes after a reload, and holds the per-file progress drawn. */
export const useDataRoomUpload = ({
  uploadLimits,
  conversationId,
  companyId,
}: Readonly<IUseDataRoomUploadParams>) => {
  const isAllowed = uploadLimits?.allowed === true;
  const storageKey = conversationId
    ? DataRoomResumeRecordHelper.resumeStorageKey(conversationId)
    : DataRoomResumeRecordHelper.newChatStorageKey(companyId);
  const { fileRows, rowsRef, updateRow, showRows, removeRow } =
    useDataRoomFileRows();
  const [readyUpload, setReadyUpload] =
    useState<IDocumentReaderUploadReference | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isStopped, setIsStopped] = useState(false);
  const [resumeRecord, setResumeRecord] =
    useState<IDataRoomResumeRecord | null>(null);
  const sessionRef = useRef<IDataRoomUploadSession | null>(null);
  // Set as soon as a room starts, before its session exists, so cancel and unmount reach creation too.
  const controllerRef = useRef<AbortController | null>(null);
  const activeRunCountRef = useRef(0);
  const lastStatusRef = useRef<DocumentReaderUploadStatus | null>(null);
  const recordRef = useRef<IDataRoomResumeRecord | null>(null);
  // Dropped but not yet shown as rows: the room being created, queued drops and the add running.
  const waitingUploadsRef = useRef<CommendaAiUploadFile[]>([]);
  const queuedDropsRef = useRef<CommendaAiUploadFile[][]>([]);
  const appendChainRef = useRef<Promise<void>>(Promise.resolve());
  const roomStorageKeyRef = useRef(storageKey);
  const wasAllowedRef = useRef(isAllowed);

  const saveRecord = useCallback(
    (record: IDataRoomResumeRecord) => {
      recordRef.current = record;
      DataRoomResumeRecordHelper.saveResumeRecord(storageKey, record);
    },
    [storageKey]
  );

  const updateRecord = useCallback<TDataRoomRecordUpdate>(
    (change) => {
      if (recordRef.current) saveRecord(change(recordRef.current));
    },
    [saveRecord]
  );

  const dropWaitingUploads = useCallback(() => {
    queuedDropsRef.current = [];
    waitingUploadsRef.current = [];
  }, []);

  /** A room that could not be created or re-read: nothing runs, and the drops waiting for it go. */
  const failStart = useCallback(
    (startError: string) => {
      controllerRef.current = null;
      dropWaitingUploads();
      setIsUploading(false);
      setError(startError);
    },
    [dropWaitingUploads]
  );

  const dropRoom = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    sessionRef.current = null;
    recordRef.current = null;
    appendChainRef.current = Promise.resolve();
    dropWaitingUploads();
    showRows([], true);
    setReadyUpload(null);
    setIsUploading(false);
    setIsStopped(false);
    setResumeRecord(null);
  }, [dropWaitingUploads, showRows]);

  // The open room's record: still the old key's in the render that changes the key.
  const resetRoom = useCallback(() => {
    dropRoom();
    DataRoomResumeRecordHelper.clearResumeRecord(roomStorageKeyRef.current);
  }, [dropRoom]);

  const finishRuns = useCallback(async () => {
    const session = sessionRef.current;
    // Send stays locked while files wait to be added.
    if (
      !session ||
      activeRunCountRef.current > 0 ||
      waitingUploadsRef.current.length > 0
    )
      return;
    setIsUploading(false);
    if (
      session.controller.signal.aborted ||
      session.files.some((file) => !session.completedFileIds.has(file.fileId))
    )
      return;
    const status =
      lastStatusRef.current === DocumentReaderUploadStatus.COMPLETED
        ? lastStatusRef.current
        : await DataRoomUploadTransfer.fetchUploadStatus(session.uploadId);
    if (sessionRef.current !== session) return;
    if (status !== DocumentReaderUploadStatus.COMPLETED) {
      // A resume has already named the files still missing; that message is the more useful one.
      setError(
        (current) =>
          current ??
          'Every file you chose is stored, but the upload is not complete yet. Choose the files still missing.'
      );
      return;
    }
    setReadyUpload(
      DataRoomUploadHelper.toUploadReference(session.uploadId, [
        ...rowsRef.current.values(),
      ])
    );
  }, [rowsRef]);

  const runFiles = useCallback(
    async (files: readonly IDataRoomLocalFile[]) => {
      const session = sessionRef.current;
      if (!session) return;
      const runControllers = DataRoomUploadTransfer.linkFileControllers(
        session,
        files
      );
      const onFileSettled = (
        file: IDataRoomLocalFile,
        outcome: IDataRoomFileOutcome
      ) => {
        // The file was removed: its removal owns the row.
        if (
          runControllers.get(file.fileId)?.signal.aborted &&
          !session.controller.signal.aborted
        )
          return;
        if (outcome.success) {
          session.completedFileIds.add(file.fileId);
          if (outcome.uploadStatus)
            lastStatusRef.current = outcome.uploadStatus;
          updateRow(
            file.fileId,
            {
              state: DataRoomFileState.STORED,
              sentBytes: file.selectedFile.file.size,
            },
            true
          );
          return;
        }
        updateRow(
          file.fileId,
          { state: DataRoomFileState.FAILED, error: outcome.error },
          true
        );
        if (outcome.isFatal && !session.controller.signal.aborted) {
          setError(outcome.error);
          setIsStopped(true);
          session.controller.abort();
        }
      };
      setIsUploading(true);
      files.forEach((file) =>
        updateRow(
          file.fileId,
          { state: DataRoomFileState.UPLOADING, error: null },
          true
        )
      );
      activeRunCountRef.current += 1;
      try {
        await DataRoomUploadTransfer.uploadFiles(
          {
            uploadId: session.uploadId,
            maxPartUrlsPerRequest: uploadLimits?.maxPartUrlsPerRequest ?? 100,
            signal: session.controller.signal,
            limitPart: session.limitPart,
            onFileBytes: (fileId, sentBytes) =>
              updateRow(fileId, { sentBytes }),
            signalFor: (fileId) =>
              runControllers.get(fileId)?.signal ?? session.controller.signal,
          },
          files,
          onFileSettled
        );
      } finally {
        activeRunCountRef.current -= 1;
      }
      if (sessionRef.current !== session) return;
      // A stopped room never starts its remaining files, so they show as failed instead of moving forever.
      if (session.controller.signal.aborted)
        files.forEach((file) => {
          if (
            rowsRef.current.get(file.fileId)?.state ===
            DataRoomFileState.UPLOADING
          )
            updateRow(file.fileId, { state: DataRoomFileState.FAILED }, true);
        });
      await finishRuns();
    },
    [uploadLimits, updateRow, finishRuns]
  );

  /** Adds a later drop to the open upload, one add at a time in drop order. A failed add keeps and
   * uploads the files already added. */
  const appendDrop = useCallback(
    (uploads: CommendaAiUploadFile[]) => {
      setReadyUpload(null);
      lastStatusRef.current = null;
      setIsUploading(true);
      const addDrop = async () => {
        const session = sessionRef.current;
        if (!session || !uploadLimits) return;
        const appended = await DataRoomUploadTransfer.addDrop({
          session,
          uploads,
          limits: uploadLimits,
          updateRecord,
        });
        if (sessionRef.current !== session) return;
        waitingUploadsRef.current = waitingUploadsRef.current.filter(
          (upload) => !uploads.includes(upload)
        );
        const files = appended?.files ?? [];
        if (appended?.success === false) setError(appended.error);
        showRows(files.map(DataRoomUploadHelper.toFileProgress), false);
        session.files = [...session.files, ...files];
        if (files.length === 0) await finishRuns();
        else runFiles(files).catch(() => setError(UNEXPECTED_STOP_ERROR));
      };
      appendChainRef.current = appendChainRef.current
        .then(addDrop)
        .catch(() => setError(UNEXPECTED_STOP_ERROR));
    },
    [uploadLimits, updateRecord, finishRuns, runFiles, showRows]
  );

  const beginSession = useCallback(
    (
      uploadId: string,
      files: IDataRoomLocalFile[],
      storedRows: readonly IDataRoomFileProgress[],
      controller: AbortController,
      isMissingFiles: boolean
    ) => {
      sessionRef.current = DataRoomUploadTransfer.createSession(
        uploadId,
        files,
        controller,
        isMissingFiles
      );
      setIsStopped(false);
      activeRunCountRef.current = 0;
      lastStatusRef.current = null;
      showRows(
        [...files.map(DataRoomUploadHelper.toFileProgress), ...storedRows],
        true
      );
      runFiles(files).catch(() => setError(UNEXPECTED_STOP_ERROR));
      const queuedDrops = queuedDropsRef.current;
      queuedDropsRef.current = [];
      queuedDrops.forEach(appendDrop);
    },
    [runFiles, appendDrop, showRows]
  );

  const resumeUpload = useCallback(
    async (
      record: IDataRoomResumeRecord,
      uploads: readonly CommendaAiUploadFile[],
      controller: AbortController
    ) => {
      const loaded = await DataRoomUploadTransfer.loadResume(
        record,
        uploads,
        controller.signal
      );
      if (controller.signal.aborted) return;
      if (loaded.success === false) {
        failStart(loaded.error);
        if (loaded.status === DocumentReaderUploadStatus.ABORTED) resetRoom();
        return;
      }
      recordRef.current = record;
      const resumeError = DataRoomUploadHelper.getResumeError(
        loaded.unmatchedNames,
        loaded.abortedNames
      );
      if (resumeError) setError(resumeError);
      // Seeded from the parts S3 holds, so only each file's missing parts are sent again.
      toast.info(
        `Resuming the document upload: ${DataRoomUploadHelper.missingPartCount(loaded.files)} parts left to send.`
      );
      beginSession(
        record.uploadId,
        loaded.files,
        loaded.storedRows,
        controller,
        loaded.unmatchedNames.length > 0
      );
      // Removed before the reload but not yet on the server.
      DataRoomUploadTransfer.removeFiles(
        record.uploadId,
        loaded.pendingRemovalFileIds
      )
        .then((removalError) => removalError && setError(removalError))
        .catch(() => undefined);
    },
    [beginSession, resetRoom, failStart]
  );

  const startUpload = useCallback(
    async (selected: readonly CommendaAiUploadFile[]) => {
      if (!isAllowed || !uploadLimits) return;
      const session = sessionRef.current;
      const dropKind = DataRoomUploadHelper.getDropKind({
        hasResumeRecord: resumeRecord !== null,
        hasSession: session !== null,
        isCreating: session === null && controllerRef.current !== null,
        isMissingFiles: session?.isMissingFiles ?? false,
        isUploading,
      });
      if (isStopped && dropKind === DataRoomDropKind.APPEND) {
        toast.error(
          'This upload has stopped, so documents cannot be added to it. Cancel it and upload the documents again.'
        );
        return;
      }
      const isResume = dropKind === DataRoomDropKind.RESUME;
      const waiting = waitingUploadsRef.current;
      // A resume pairs the files chosen again with the rows already shown, so they are not duplicates.
      const { uploads, emptyNames, duplicateNames, limitError } =
        DataRoomUploadHelper.checkDrop(
          selected,
          uploadLimits,
          isResume
            ? null
            : { rows: [...rowsRef.current.values()], waitingUploads: waiting }
        );
      if (emptyNames.length > 0)
        toast.warning(
          `Skipped empty files: ${DataRoomUploadHelper.formatNameList(emptyNames)}`
        );
      if (duplicateNames.length > 0)
        toast.info(
          `Skipped documents already added: ${DataRoomUploadHelper.formatNameList(duplicateNames)}.`
        );
      setError(limitError);
      if (limitError || uploads.length === 0) return;

      if (
        dropKind === DataRoomDropKind.QUEUE ||
        dropKind === DataRoomDropKind.APPEND
      ) {
        waitingUploadsRef.current = [...waiting, ...uploads];
        if (dropKind === DataRoomDropKind.APPEND) appendDrop(uploads);
        else queuedDropsRef.current = [...queuedDropsRef.current, uploads];
        return;
      }
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setIsUploading(true);
      if (isResume) {
        sessionRef.current = null;
        const record = recordRef.current ?? resumeRecord;
        if (record) await resumeUpload(record, uploads, controller);
        return;
      }
      waitingUploadsRef.current = uploads;
      const created = await DataRoomUploadTransfer.createRoom({
        uploads,
        limits: uploadLimits,
        conversationId,
        companyId,
        signal: controller.signal,
        onRecordChange: saveRecord,
      });
      if (controller.signal.aborted) {
        // Cancelled or left while the room was being created, so nothing uploads in the background.
        if (created.success)
          abortDocumentReaderUpload(created.uploadId).catch(() => undefined);
        DataRoomResumeRecordHelper.clearResumeRecord(storageKey);
        return;
      }
      waitingUploadsRef.current = waitingUploadsRef.current.filter(
        (upload) => !uploads.includes(upload)
      );
      if (created.success === false) {
        failStart(created.error);
        DataRoomResumeRecordHelper.clearResumeRecord(storageKey);
        return;
      }
      if (created.skippedEmptyFileNames.length > 0)
        toast.warning(
          `Skipped empty files: ${DataRoomUploadHelper.formatNameList(created.skippedEmptyFileNames)}`
        );
      beginSession(created.uploadId, created.files, [], controller, false);
    },
    [
      isAllowed,
      uploadLimits,
      conversationId,
      companyId,
      storageKey,
      resumeRecord,
      isUploading,
      isStopped,
      resumeUpload,
      beginSession,
      appendDrop,
      saveRecord,
      failStart,
    ]
  );

  /** Re-runs one failed file; only the parts S3 does not hold are sent again. */
  const retryFile = useCallback(
    (fileId: string) => {
      const session = sessionRef.current;
      const file = session?.files.find(
        (candidate) => candidate.fileId === fileId
      );
      if (!session || !file || session.controller.signal.aborted) return;
      setError(null);
      runFiles([file]).catch(() => setError(UNEXPECTED_STOP_ERROR));
    },
    [runFiles]
  );

  /** Aborts an unfinished upload on the server too; a stored room is only dropped from this message. */
  const cancelUpload = useCallback(() => {
    const uploadId = readyUpload
      ? null
      : (sessionRef.current?.uploadId ?? resumeRecord?.uploadId ?? null);
    resetRoom();
    setError(null);
    if (uploadId)
      abortDocumentReaderUpload(uploadId)
        .then((aborted) => {
          if (!aborted.success)
            toast.error(
              aborted.error ?? 'Could not cancel the document upload.'
            );
        })
        .catch(() => undefined);
  }, [readyUpload, resumeRecord, resetRoom]);

  // Declared before the key effect: switching agent also changes the key, and the room is cancelled first.
  useEffect(() => {
    const wasAllowed = wasAllowedRef.current;
    wasAllowedRef.current = isAllowed;
    if (!wasAllowed || isAllowed) return;
    setError(null);
    if (rowsRef.current.size === 0 && controllerRef.current === null) return;
    cancelUpload();
    toast.info(READER_OFF_NOTICE);
  }, [isAllowed, cancelUpload, rowsRef]);

  // sessionStorage is read in an effect, not during render, so the server render matches the first client one.
  useEffect(() => {
    // Another conversation or workspace starts empty; the old room keeps its record for its own resume.
    if (roomStorageKeyRef.current !== storageKey) {
      roomStorageKeyRef.current = storageKey;
      dropRoom();
      setError(null);
    }
    setResumeRecord(DataRoomResumeRecordHelper.readResumeRecord(storageKey));
    return () => controllerRef.current?.abort();
  }, [storageKey, dropRoom]);

  /** Removes one file; the last one left cancels the whole upload. A refused removal keeps its pill. */
  const removeFile = useCallback(
    async (fileId: string) => {
      const session = sessionRef.current;
      if (!session || session.removingFileIds.has(fileId)) return;
      if (
        DataRoomUploadHelper.isLastRemainingFile(
          rowsRef.current.size,
          waitingUploadsRef.current.length
        )
      ) {
        cancelUpload();
        return;
      }
      session.removingFileIds.add(fileId);
      setReadyUpload(null);
      updateRecord((record) =>
        DataRoomResumeRecordHelper.withRemovedFile(record, fileId)
      );
      const removed = await DataRoomUploadTransfer.removeFile(
        session.uploadId,
        fileId,
        session.fileControllers.get(fileId) ?? null
      );
      session.removingFileIds.delete(fileId);
      if (sessionRef.current !== session) return;
      if (removed.success === false) {
        updateRecord((record) =>
          DataRoomResumeRecordHelper.withoutRemovedFile(record, fileId)
        );
        setError(removed.error);
        // Its parts were stopped, so a file not yet stored shows as failed and its retry restarts it.
        if (rowsRef.current.get(fileId)?.state !== DataRoomFileState.STORED)
          updateRow(
            fileId,
            { state: DataRoomFileState.FAILED, error: removed.error },
            true
          );
      } else if (removed.upload.status === DocumentReaderUploadStatus.ABORTED) {
        resetRoom();
        return;
      } else {
        DataRoomUploadTransfer.forgetFile(session, fileId);
        lastStatusRef.current = removed.upload.status;
        removeRow(fileId);
      }
      await finishRuns();
    },
    [cancelUpload, updateRecord, updateRow, resetRoom, finishRuns, removeRow]
  );

  const progress = useMemo(
    () =>
      fileRows.length > 0 ? DataRoomUploadHelper.summarise(fileRows) : null,
    [fileRows]
  );

  return {
    isAllowed,
    progress,
    readyUpload,
    isUploading,
    isPreparing: isUploading && fileRows.length === 0,
    error,
    resumeFileCount:
      isAllowed && !isUploading && fileRows.length === 0 && resumeRecord
        ? DataRoomResumeRecordHelper.expectedFileCount(resumeRecord)
        : null,
    startUpload,
    retryFile: isStopped ? null : retryFile,
    cancelUpload,
    removeFile,
    clearAfterSend: resetRoom,
  };
};
